import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { ParamParseKey, useNavigate, useParams } from 'react-router-dom';

import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Breadcrumb, BreadcrumbLink } from '@servicetitan/design-system';

import { withRoot } from '../../utils/with-root';
import { PartitionSidebar } from './partition-sidebar';
import { AccountManagementRoutes, ManageTenantRoutes } from '../../utils/routes';
import { LoadStatus } from '../../../common/enums/load-status';
import { PartitionConfiguration } from './partition-configuration';
import { ManagePartitionStore } from '../../stores/manage-partition.store';
import { ManageTenantLayout } from '../manage-tenant-layout/manage-tenant-layout';
import { useManageTenantGeneratePath } from '../../utils/use-manage-tenant-generate-path';
import { ManageTenantStore } from '../../stores/manage-tenant.store';

export const ManagePartition = provide({ singletons: [ManageTenantStore, ManagePartitionStore] })(
    observer(() => {
        const [{ init, pageLoadStatus, tenantName }] = useDependencies(ManagePartitionStore);

        const { externalTenantId, externalPartnerId, externalPartitionId } =
            useParams<ParamParseKey<typeof AccountManagementRoutes.managePartition>>();

        const navigate = useNavigate();

        useEffect(() => {
            init(externalPartnerId!, externalTenantId!, externalPartitionId!);
        }, [externalPartnerId, externalTenantId, externalPartitionId, init]);

        const { generateManageTenantPathRelative } = useManageTenantGeneratePath();

        return (
            <ManageTenantLayout
                panelContent={<PartitionSidebar />}
                error={pageLoadStatus === LoadStatus.Error}
                loading={pageLoadStatus === LoadStatus.Loading}
                breadCrumb={
                    <Breadcrumb>
                        <BreadcrumbLink
                            label="Accounts Management"
                            onClick={() => navigate(withRoot(AccountManagementRoutes.tenants))}
                        />
                        <BreadcrumbLink
                            label="Manage Tenant"
                            onClick={() =>
                                navigate(
                                    withRoot(
                                        generateManageTenantPathRelative(
                                            ManageTenantRoutes.partitions,
                                        ),
                                    ),
                                )
                            }
                        />
                        <BreadcrumbLink label="Manage Partition" />
                    </Breadcrumb>
                }
                headline={tenantName}
            >
                <PartitionConfiguration />
            </ManageTenantLayout>
        );
    }),
);
